import React, { useState, useEffect, useCallback } from "react";
import api from "../../api/api";
import { useTheme } from "../../hooks/useTheme";
import { useNavigate } from "react-router-dom";
import CoachingTabs from "./CoachingTabs";
import CoachingSkeleton from "../../components/layout/CoachingSkeleton";
import {
    Users, UserMinus, Mail, Calendar,
    Search, CheckCircle2, XCircle, ArrowRight
} from "lucide-react";

export default function MyMentor() {
    const { isDarkMode } = useTheme();
    const navigate = useNavigate();
    const [mentor, setMentor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [message, setMessage] = useState(null);

    const fetchMentor = useCallback(async () => {
        try { const res = await api.get("/coach/my-mentor"); setMentor(res.data.mentor || null); } catch { setMentor(null); }
    }, []);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            await fetchMentor();
            setLoading(false);
        };
        load();
    }, [fetchMentor]);

    const showMessage = (text, type = "success") => {
        setMessage({ text, type });
        setTimeout(() => setMessage(null), 4000);
    };

    const removeMentor = async () => {
        if (!window.confirm("Are you sure you want to remove your coach?")) return;
        setActionLoading(true);
        try {
            await api.delete("/coach/my-mentor");
            setMentor(null);
            showMessage("Coach removed");
        } catch (err) {
            showMessage(err.response?.data?.error || "Failed to remove coach", "error");
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) {
        return <CoachingSkeleton isDarkMode={isDarkMode} />;
    }

    return (
        <div className={`min-h-screen ${isDarkMode ? "bg-surface" : "bg-surface"}`}>
            <div className="max-w-5xl mx-auto px-4 py-6 md:py-8">
                <header className="mb-6">
                    <h1 className={`text-2xl md:text-3xl font-extrabold tracking-tight mb-2 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                        My Partner Coach
                    </h1>
                    <p className={isDarkMode ? "text-slate-400" : "text-slate-600"}>
                        Your assigned coach for personalized career guidance
                    </p>
                </header>

                <CoachingTabs className="md:hidden mb-6" />

                {message && (
                    <div role="alert" className={`mb-5 flex items-center gap-3 p-3.5 rounded-xl text-sm font-medium transition-all ${
                        message.type === "error"
                            ? isDarkMode ? "bg-red-900/30 text-red-300 border border-red-800/50" : "bg-red-50 text-red-800 border border-red-200"
                            : isDarkMode ? "bg-primary-900/30 text-primary-300 border border-primary-800/50" : "bg-primary-50 text-primary-800 border border-primary-200"
                    }`}>
                        {message.type === "error" ? <XCircle size={18} /> : <CheckCircle2 size={18} />}
                        {message.text}
                    </div>
                )}

                {mentor ? (
                    <div className={`rounded-2xl border overflow-hidden ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200"} shadow-sm`}>
                        <div className="h-24 bg-gradient-to-r from-primary-500 via-teal-500 to-cyan-500 relative">
                            <div className="absolute -bottom-10 left-6">
                                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary-500 to-teal-600 flex items-center justify-center text-white text-2xl font-bold border-4 border-white dark:border-slate-800 shadow-lg">
                                    {mentor.FirstName?.[0]}{mentor.LastName?.[0]}
                                </div>
                            </div>
                        </div>
                        <div className="pt-14 px-4 md:px-6 pb-6">
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                                <div className="min-w-0">
                                    <h2 className={`text-xl font-bold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                                        {mentor.FirstName} {mentor.LastName}
                                    </h2>
                                    <p className={`text-sm mt-0.5 ${isDarkMode ? "text-primary-400" : "text-primary-600"} font-medium`}>Your Coach</p>
                                    <div className="flex flex-wrap gap-4 mt-3">
                                        <div className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                                            <Mail size={14} className="shrink-0 text-slate-400" />
                                            <span className="break-all">{mentor.Email}</span>
                                        </div>
                                        <div className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                                            <Calendar size={14} className="text-slate-400" />
                                            <span>Joined {mentor.JoinedDate}</span>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={removeMentor}
                                    disabled={actionLoading}
                                    aria-label="Remove coach"
                                    className={`self-start shrink-0 flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 ${
                                        isDarkMode
                                            ? "bg-red-900/30 text-red-400 hover:bg-red-900/50 border border-red-800/50"
                                            : "bg-red-50 text-red-700 hover:bg-red-100 border border-red-200"
                                    }`}
                                >
                                    <UserMinus size={16} />
                                    Remove
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className={`rounded-2xl border p-12 text-center ${isDarkMode ? "bg-slate-800/60 border-slate-700/60" : "bg-white border-slate-200"}`}>
                        <div className={`w-20 h-20 mx-auto mb-5 rounded-2xl flex items-center justify-center ${isDarkMode ? "bg-slate-700/60" : "bg-slate-100"}`}>
                            <Users className={isDarkMode ? "text-slate-500" : "text-slate-300"} size={36} />
                        </div>
                        <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? "text-slate-200" : "text-slate-800"}`}>
                            No coach assigned yet
                        </h3>
                        <p className={`text-sm mb-6 max-w-sm mx-auto ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                            Browse available coaches and send a request to start your personalized coaching journey.
                        </p>
                        <button
                            onClick={() => navigate("/coaching/find")}
                            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold bg-primary-600 text-white hover:bg-primary-700 transition-colors shadow-sm shadow-primary-600/20"
                        >
                            <Search size={16} />
                            Find a Partner Coach
                            <ArrowRight size={14} />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
